import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../providers/api-rest-provider';

const DISMISSED_KEY = 'dismissedSetupGuide';

interface TenantSelf {
  orgName: string | null;
  checklist: { key: string; complete: boolean }[];
  seats: { used: number; total: number } | null;
}

async function fetchTenantSelf(): Promise<TenantSelf> {
  const raw = await apiFetch('tenants/self');
  return {
    orgName: raw?.name ?? null,
    checklist: Object.entries(raw?.checklist ?? {}).map(([key, complete]) => ({
      key,
      complete: Boolean(complete),
    })),
    seats: raw?.seats ? { used: raw.seats.used, total: raw.seats.total } : null,
  };
}

interface WorkflowStep {
  id: string;
  step: number;
  label: string;
  fullLabel: string;
  description: string;
  actionLabel: string;
  actionPath: string;
  checklistKeys: string[];
}

const WORKFLOW_STEPS: WorkflowStep[] = [
  {
    id: 'build',
    step: 1,
    label: 'Build',
    fullLabel: 'Build Your Foundation',
    description:
      'Define your org hierarchy, scoring standards, and the physical events your program administers. This is where everything begins.',
    actionLabel: 'Go to Org Units →',
    actionPath: '/org_units',
    checklistKeys: ['org_setup', 'org_tree'],
  },
  {
    id: 'staff',
    step: 2,
    label: 'Staff',
    fullLabel: 'Staff Your Program',
    description:
      'Add the administrators, instructors, and participants who make up your program.',
    actionLabel: 'Go to Staff →',
    actionPath: '/users',
    checklistKeys: ['admins_invited', 'roster_imported'],
  },
  {
    id: 'assess',
    step: 3,
    label: 'Assess',
    fullLabel: 'Design Assessments',
    description:
      'Bundle events into formal assessments and schedule them across your command.',
    actionLabel: 'Go to Assessments →',
    actionPath: '/assessments',
    checklistKeys: ['assessment_created'],
  },
  {
    id: 'record',
    step: 4,
    label: 'Record',
    fullLabel: 'Record Results',
    description:
      'Log event attempts and use governed participation workflows for explicit event-scoped declarations.',
    actionLabel: 'Go to Event Attempts →',
    actionPath: '/attempts',
    checklistKeys: [],
  },
  {
    id: 'review',
    step: 5,
    label: 'Review',
    fullLabel: 'Review Performance',
    description:
      'Analyze pass rates, trend data, and rankings across your entire command.',
    actionLabel: 'View Dashboard →',
    actionPath: '/',
    checklistKeys: [],
  },
];

function buildChecklistMap(checklist: { key: string; complete: boolean }[]): Record<string, boolean> {
  return checklist.reduce<Record<string, boolean>>((acc, item) => {
    acc[item.key] = item.complete;
    return acc;
  }, {});
}

function isStepComplete(step: WorkflowStep, index: number, stepCompletion: boolean[]): boolean {
  // Steps 4 and 5 have no checklist keys — complete only when all prior steps are done
  if (step.checklistKeys.length === 0) {
    return stepCompletion.slice(0, index).every(Boolean);
  }
  return stepCompletion[index];
}

function computeStepCompletion(checklistMap: Record<string, boolean>): boolean[] {
  const rawCompletion = WORKFLOW_STEPS.map((step) => {
    if (step.checklistKeys.length === 0) return false; // placeholder; resolved below
    return step.checklistKeys.every((key) => checklistMap[key] === true);
  });

  // Resolve steps 4 and 5 based on prior completion
  return WORKFLOW_STEPS.map((step, index) => isStepComplete(step, index, rawCompletion));
}

export const SetupGuide = () => {
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantSelf | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(
    () => localStorage.getItem(DISMISSED_KEY) === '1',
  );

  useEffect(() => {
    if (dismissed) return;
    fetchTenantSelf()
      .then(setTenant)
      .catch(() => {
        // Silently fail — don't block the app
      });
  }, [dismissed]);

  if (dismissed) return null;
  if (!tenant) return null;

  const checklistMap = buildChecklistMap(tenant.checklist);
  const stepCompletion = computeStepCompletion(checklistMap);
  const allComplete = stepCompletion.every(Boolean);

  // If checklist has no incomplete items at all, render null (guide not needed)
  const hasAnyIncomplete = tenant.checklist.some((c) => !c.complete);
  if (!hasAnyIncomplete && !allComplete) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  // Find the first incomplete step (0-based index)
  const firstIncompleteIndex = stepCompletion.findIndex((complete) => !complete);
  const activeStep = firstIncompleteIndex === -1 ? WORKFLOW_STEPS.length : firstIncompleteIndex;
  const focusedStep = firstIncompleteIndex === -1 ? null : WORKFLOW_STEPS[firstIncompleteIndex];

  if (allComplete) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircleIcon sx={{ color: 'success.main' }} />
              <Typography variant="body1" color="success.main" fontWeight={600}>
                Setup complete — your program is ready.
              </Typography>
            </Box>
            <Button variant="outlined" size="small" onClick={handleDismiss}>
              Dismiss
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title={
          <Typography variant="h6" fontWeight={700}>
            Your CHAMBER Workflow
          </Typography>
        }
        action={
          <IconButton size="small" onClick={handleDismiss} aria-label="Dismiss setup guide">
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {WORKFLOW_STEPS.map((step, index) => {
            const complete = stepCompletion[index];
            return (
              <Step key={step.id} completed={complete}>
                <StepLabel
                  StepIconComponent={
                    complete
                      ? () => (
                          <CheckCircleIcon
                            sx={{ color: 'success.main', fontSize: 24 }}
                          />
                        )
                      : undefined
                  }
                >
                  {step.label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>

        {focusedStep && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              pt: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Step {focusedStep.step}: {focusedStep.fullLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {focusedStep.description}
            </Typography>
            <Box mt={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(focusedStep.actionPath)}
              >
                {focusedStep.actionLabel}
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
